import { useNavigate } from 'react-router-dom';
import { ExternalLink, Receipt } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import { PLAN_NAME_KEYS } from '../../i18n';
import type { PaymentRecord } from '../../api/subscription.api';
import { LOGIN_PATH, PRICING_PATH } from '../../constants/routes';
import { formatDate } from '../../utils/format';

function statusColor(status: PaymentRecord['status']): string {
  if (status === 'completed') return 'bg-emerald-500/20 text-emerald-400';
  if (status === 'pending') return 'bg-amber-500/20 text-amber-400';
  return 'bg-rose-500/20 text-rose-400';
}

interface PaymentsPageProps {
  payments: PaymentRecord[];
  loading: boolean;
  error: string | null;
}

export function PaymentsPage({ payments, loading, error }: Readonly<PaymentsPageProps>) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="text-center py-6">
        <p className="text-xs text-slate-500 mb-3">{t('payments.signInPrompt')}</p>
        <button
          onClick={() => navigate(LOGIN_PATH)}
          className="text-xs text-violet-400 font-semibold hover:underline"
        >
          {t('common.signIn')}
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="text-xs text-slate-400 text-center py-6">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="text-xs text-rose-500 text-center py-6">{error}</div>;
  }

  if (payments.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700 mb-3 mx-auto">
          <Receipt className="h-5 w-5 text-slate-500" />
        </div>
        <p className="text-sm font-semibold text-slate-400">{t('payments.emptyTitle')}</p>
        <p className="text-xs text-slate-500 mt-1">{t('payments.emptyBody')}</p>
        <button
          onClick={() => navigate(PRICING_PATH)}
          className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500 transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t('payments.viewPlans')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-w-xl">
      {payments.map((p) => (
        <div
          key={p.id}
          className="rounded-xl border border-slate-700 bg-slate-800 p-3 flex items-center justify-between gap-2"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white truncate">
              {t('subscription.planLabel', { plan: t(PLAN_NAME_KEYS[p.plan]) })}
            </p>
            <p className="text-xs text-slate-500 truncate">{formatDate(p.createdAt)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-bold text-white">৳{p.amount}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusColor(p.status)}`}
            >
              {t(`status.${p.status}`)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
